import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme, styled } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Grid,
    Stack,
    Typography,
    Link,
    useMediaQuery,
    Box,
    Button,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormHelperText,
    TextField
} from '@mui/material';

// project imports
import AuthCardWrapper from 'components/Cards/AuthCardWrapper';
import logoImg from 'assets/images/raptor_tech_logo.png';
import { authLogin } from 'components/../redux/actions/auth';
import ApiHelper from 'utils/apiHelper';
import { API_URL } from 'utils/constants';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const AuthWrapper = styled('div')(({ theme }) => ({
    background: `linear-gradient(white, ${theme.palette.primary.main})`
    // minHeight: '100vh'
}));

const Login = () => {
    const { userInfo } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const theme = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [lengthLimits, setLengthLimits] = useState({
        usernameMaxLength: 255,
        passwordMaxLength: 255
    });

    const [ssoOpen, setSsoOpen] = useState(false);
    const [ssoEmail, setSsoEmail] = useState('');
    const [ssoChecking, setSsoChecking] = useState(false);
    const [ssoMessage, setSsoMessage] = useState('');

    const openSsoDialog = () => {
        setSsoMessage('');
        setSsoOpen(true);
    };

    const handleSsoContinue = async () => {
        setSsoMessage('');
        if (!EMAIL_RE.test(ssoEmail.trim())) {
            setSsoMessage('Please enter a valid email address.');
            return;
        }
        setSsoChecking(true);
        try {
            const res = await ApiHelper.get(`${API_URL.SSO_CHECK}?email=${encodeURIComponent(ssoEmail.trim())}`, {}, {}, {}, false);
            if (res.data && res.data.sso && res.data.redirect) {
                // Full-page navigation: the backend builds the SAML AuthnRequest and
                // redirects on to the identity provider.
                window.location.href = res.data.redirect;
            } else {
                setSsoChecking(false);
                setSsoMessage('Single sign-on is not available for this email. Please sign in with your username and password.');
            }
        } catch (err) {
            setSsoChecking(false);
            setSsoMessage('Could not start single sign-on. Please try again, or sign in with your username and password.');
        }
    };

    useEffect(() => {
        if (userInfo) {
            if (userInfo.admin_permissions.root_admin) {
                navigate('/management/organizations');
            } else {
                navigate('/home');
            }
        }
    }, [navigate, userInfo]);

    useEffect(() => {
        const headers = {
            'Content-Type': 'application/json'
        };
        // Fetch security settings when component mounts
        ApiHelper.post('/api/auth/security_settings/', {}, headers, {}, false)
            .then((res) => {
                if (res.data && res.data.passwordSettings) {
                    setLengthLimits({
                        usernameMaxLength: res.data.passwordSettings.usernameMaxLength || 50,
                        passwordMaxLength: res.data.passwordSettings.passwordMaxLength || 50
                    });
                }
            })
            .catch((err) => {
                console.error('Failed to fetch security settings:', err);
            });
    }, []);

    const formik = useFormik({
        initialValues: {
            username: '',
            password: '',
            submit: null
        },
        validationSchema: Yup.object({
            username: Yup.string()
                .max(lengthLimits.usernameMaxLength, `Username must be at most ${lengthLimits.usernameMaxLength} characters`)
                .required('Username is required'),
            password: Yup.string()
                .max(lengthLimits.passwordMaxLength, `Password must be at most ${lengthLimits.passwordMaxLength} characters`)
                .required('Password is required')
        }),
        onSubmit: (values, helpers) => {
            setLoading(true);
            dispatch(authLogin({ username: values.username, password: values.password }, navigate))
                .then(() => setLoading(false))
                .catch(() => setLoading(false));
        }
    });

    return (
        <AuthWrapper>
            <Grid container direction="column" justifyContent="flex-end" sx={{ minHeight: '100vh' }}>
                <Grid item xs={12}>
                    <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 'calc(100vh - 68px)' }}>
                        <Grid item sx={{ m: { xs: 1, sm: 3 }, mb: 0 }}>
                            <AuthCardWrapper>
                                <Grid container spacing={2} alignItems="center" justifyContent="center">
                                    <Grid item sx={{ mb: 3 }}>
                                        <Link href="/" aria-label="WakeSMART Administration Login">
                                            <img src={logoImg} width="150" alt="" />
                                        </Link>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Grid
                                            container
                                            direction={matchDownSM ? 'column-reverse' : 'row'}
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Grid item>
                                                <Stack alignItems="center" justifyContent="center" spacing={1}>
                                                    <Typography
                                                        color={theme.palette.primary.main}
                                                        gutterBottom
                                                        variant={matchDownSM ? 'h3' : 'h2'}
                                                    >
                                                        WakeSMART Administration Login
                                                    </Typography>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <form noValidate onSubmit={formik.handleSubmit}>
                                            <TextField
                                                error={Boolean(formik.touched.username && formik.errors.username)}
                                                fullWidth
                                                helperText={formik.touched.username && formik.errors.username}
                                                label="Username"
                                                margin="normal"
                                                name="username"
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                                type="username"
                                                value={formik.values.username}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                            <TextField
                                                error={Boolean(formik.touched.password && formik.errors.password)}
                                                fullWidth
                                                helperText={formik.touched.password && formik.errors.password}
                                                label="Password"
                                                margin="normal"
                                                name="password"
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                                type="password"
                                                value={formik.values.password}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                            {formik.errors.submit && (
                                                <Box sx={{ mt: 3 }}>
                                                    <FormHelperText error>{formik.errors.submit}</FormHelperText>
                                                </Box>
                                            )}
                                            <Box sx={{ mt: 2, fontFamily: 'sans-serif', fontSize: 8 }}>
                                                <Link href="/login/forgot_password" sx={{ float: 'right', textDecoration: 'none', mb: 1 }}>
                                                    Forgot Password?
                                                </Link>
                                                <LoadingButton
                                                    // disabled={formik.isSubmitting}
                                                    fullWidth
                                                    size="large"
                                                    type="submit"
                                                    variant="contained"
                                                    loading={loading}
                                                >
                                                    Log In
                                                </LoadingButton>
                                            </Box>
                                        </form>
                                    </Grid>
                                </Grid>
                            </AuthCardWrapper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </AuthWrapper>
    );
};

export default Login;
